import type { Func } from '../types/function';

export interface Debounced<F extends Func> {
  (...args: Parameters<F>): void;
  cancel: () => void;
  flush: () => ReturnType<F> | undefined;
}

interface DebounceOptions {
  leading?: boolean;
  trailing?: boolean;
}

export function debounce<F extends Func>(func: F, wait: number, options?: DebounceOptions): Debounced<F> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let lastArgs: Parameters<F> | undefined;
  let lastResult: ReturnType<F> | undefined;

  const leading = options?.leading ?? false;
  const trailing = options?.trailing ?? true;

  const invoke = () => {
    if (!lastArgs) {
      return;
    }

    lastResult = func(...lastArgs);
    lastArgs = undefined;
  };

  const debounced = (...args: Parameters<F>) => {
    lastArgs = args;

    const shouldCallLeading = leading && !timeout;

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      timeout = undefined;

      if (trailing) {
        invoke();
      }
    }, wait);

    if (shouldCallLeading) {
      invoke();
    }
  };

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = undefined;
    lastArgs = undefined;
  };

  debounced.flush = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
      invoke();
    }

    return lastResult;
  };

  return debounced;
}
